// courses/{id} documents and the cover images in the aura-courses Storage
// bucket. Moved from aura-courses-admin with `db`/`storage` swapped for the
// courses-app instances; the logic, including the client-side totals
// recomputation, is unchanged.

import {
  addDoc,
  collection,
  doc,
  getDoc,
  getDocs,
  orderBy,
  query,
  Timestamp,
  updateDoc,
  where,
  writeBatch,
  type DocumentSnapshot,
  type UpdateData,
} from "firebase/firestore";
import { deleteObject, getDownloadURL, ref, uploadBytes } from "firebase/storage";
import { getCoursesFirestore, getCoursesStorage } from "../../config/firebase";
import type { Course, CourseStatus } from "../../types/course";

export const COURSES_COLLECTION = "courses";
export const LESSONS_COLLECTION = "lessons";
export const VIDEOS_COLLECTION = "videos";

function toCourse(snap: DocumentSnapshot): Course {
  const data = snap.data() ?? {};
  return {
    id: snap.id,
    ...data,
    createdAt: data.createdAt?.toDate(),
    updatedAt: data.updatedAt?.toDate(),
  } as Course;
}

// Number of lesson documents; 0 when the read fails.
export async function calculateTotalLessons(courseId: string): Promise<number> {
  try {
    const snapshot = await getDocs(collection(getCoursesFirestore(), COURSES_COLLECTION, courseId, LESSONS_COLLECTION));
    return snapshot.size;
  } catch (error) {
    console.error("Error calculating total lessons:", error);
    return 0;
  }
}

// Sum of every video duration plus every knowledge-check duration (minutes).
export async function calculateTotalDuration(courseId: string): Promise<number> {
  try {
    const db = getCoursesFirestore();
    const lessonsSnapshot = await getDocs(collection(db, COURSES_COLLECTION, courseId, LESSONS_COLLECTION));
    let totalDuration = 0;
    for (const lessonDoc of lessonsSnapshot.docs) {
      const videosSnapshot = await getDocs(
        collection(db, COURSES_COLLECTION, courseId, LESSONS_COLLECTION, lessonDoc.id, VIDEOS_COLLECTION),
      );
      videosSnapshot.docs.forEach((videoDoc) => {
        totalDuration += videoDoc.data().duration || 0;
      });
      const knowledgeCheck = lessonDoc.data().knowledgeCheck;
      if (knowledgeCheck?.duration) totalDuration += knowledgeCheck.duration;
    }
    return totalDuration;
  } catch (error) {
    console.error("Error calculating total duration:", error);
    return 0;
  }
}

// Called after every lesson or video change.
export async function updateCourseTotals(courseId: string): Promise<void> {
  const totalLessons = await calculateTotalLessons(courseId);
  const totalDuration = await calculateTotalDuration(courseId);
  await updateDoc(doc(getCoursesFirestore(), COURSES_COLLECTION, courseId), {
    totalLessons,
    totalDuration,
    updatedAt: Timestamp.now(),
  });
}

export async function getAllCourses(): Promise<Course[]> {
  const q = query(collection(getCoursesFirestore(), COURSES_COLLECTION), orderBy("createdAt", "desc"));
  const snapshot = await getDocs(q);
  return snapshot.docs.map(toCourse);
}

export async function getCoursesByStatus(status: CourseStatus): Promise<Course[]> {
  const q = query(
    collection(getCoursesFirestore(), COURSES_COLLECTION),
    where("status", "==", status),
    orderBy("createdAt", "desc"),
  );
  const snapshot = await getDocs(q);
  return snapshot.docs.map(toCourse);
}

export async function getCourseById(id: string): Promise<Course | null> {
  const snap = await getDoc(doc(getCoursesFirestore(), COURSES_COLLECTION, id));
  return snap.exists() ? toCourse(snap) : null;
}

export async function createCourse(
  courseData: Omit<Course, "id" | "createdAt" | "updatedAt" | "totalLessons" | "totalDuration">,
): Promise<string> {
  const newCourse = {
    ...courseData,
    totalLessons: 0,
    totalDuration: 0,
    createdAt: Timestamp.now(),
    updatedAt: Timestamp.now(),
  };
  const docRef = await addDoc(collection(getCoursesFirestore(), COURSES_COLLECTION), newCourse);
  return docRef.id;
}

export async function updateCourse(id: string, courseData: Partial<Course>): Promise<void> {
  const updateData: UpdateData<Course> = {
    ...courseData,
    updatedAt: Timestamp.now(),
  };
  // Totals and creation time are never set by hand.
  delete updateData.totalLessons;
  delete updateData.totalDuration;
  delete updateData.createdAt;
  delete updateData.id;
  await updateDoc(doc(getCoursesFirestore(), COURSES_COLLECTION, id), updateData);
}

// Deletes the course with all its lessons and videos in one batch.
export async function deleteCourse(id: string): Promise<void> {
  const db = getCoursesFirestore();
  const batch = writeBatch(db);
  const lessonsSnapshot = await getDocs(collection(db, COURSES_COLLECTION, id, LESSONS_COLLECTION));
  for (const lessonDoc of lessonsSnapshot.docs) {
    const videosSnapshot = await getDocs(
      collection(db, COURSES_COLLECTION, id, LESSONS_COLLECTION, lessonDoc.id, VIDEOS_COLLECTION),
    );
    videosSnapshot.docs.forEach((videoDoc) => batch.delete(videoDoc.ref));
    batch.delete(lessonDoc.ref);
  }
  batch.delete(doc(db, COURSES_COLLECTION, id));
  await batch.commit();
}

export async function updateCourseStatus(id: string, status: CourseStatus): Promise<void> {
  await updateDoc(doc(getCoursesFirestore(), COURSES_COLLECTION, id), {
    status,
    updatedAt: Timestamp.now(),
  });
}

// Uploads to `${path}/${timestamp}_${name}` in the courses bucket and returns
// the download URL stored on the course document.
export async function uploadImage(file: File, path: string): Promise<string> {
  const storageRef = ref(getCoursesStorage(), `${path}/${Date.now()}_${file.name}`);
  await uploadBytes(storageRef, file);
  return getDownloadURL(storageRef);
}

export async function deleteImage(imageUrl: string): Promise<void> {
  await deleteObject(ref(getCoursesStorage(), imageUrl));
}
